import type { BjCue } from '../../../shared/games/blackjack/view.js';

// The blackjack table's sounds (2026-10-01): the server schedules the commentary and the card sounds at
// server times; every page plays them at that moment (the clock offset is measured from each answer).
// The background music loops quietly under them. Both can be switched off (remembered in the browser).

const MUSIC = '/blackjack/aanet/musiikki.mp3';
const KEY = 'areena.blackjack.sound';

interface Prefs {
  sound: boolean;
  music: boolean;
}

function loadPrefs(): Prefs {
  try {
    return { sound: true, music: true, ...(JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Prefs>) };
  } catch {
    return { sound: true, music: true };
  }
}

export class BjAudio {
  private ctx: AudioContext | null = null;
  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private handled = new Set<number>();
  private offset = 0;
  private music: { source: AudioBufferSourceNode; gain: GainNode } | null = null;
  prefs: Prefs = loadPrefs();

  /** Browsers start sound only after a tap: the first tap anywhere starts it. */
  start(): void {
    if (typeof AudioContext === 'undefined') return;
    this.ctx ??= new AudioContext();
    const resume = () => void this.ctx!.resume().then(() => this.syncMusic());
    resume();
    if (this.ctx.state !== 'running') {
      addEventListener('pointerdown', resume, { once: true, capture: true });
      addEventListener('keydown', resume, { once: true, capture: true });
    }
  }

  get blocked(): boolean {
    return !this.ctx || this.ctx.state !== 'running';
  }

  stop(): void {
    this.stopMusic();
    void this.ctx?.close();
    this.ctx = null;
  }

  set(next: Partial<Prefs>): void {
    this.prefs = { ...this.prefs, ...next };
    try {
      localStorage.setItem(KEY, JSON.stringify(this.prefs));
    } catch {
      // not remembered
    }
    this.syncMusic();
  }

  /** Server time now (by the latest answer). */
  serverNow(): number {
    return Date.now() + this.offset;
  }

  private load(src: string): Promise<AudioBuffer | null> {
    let p = this.buffers.get(src);
    if (!p) {
      p = fetch(src)
        .then((r) => r.arrayBuffer())
        .then((b) => this.ctx!.decodeAudioData(b))
        .catch(() => null);
      this.buffers.set(src, p);
    }
    return p;
  }

  /** A new answer: the clock offset and the cues still to come. */
  update(serverNow: number, cues: BjCue[]): void {
    const measured = serverNow - Date.now();
    this.offset = this.offset === 0 ? measured : this.offset * 0.8 + measured * 0.2;
    if (!this.ctx) return;
    for (const c of cues) {
      if (this.handled.has(c.id)) continue;
      // Already over (e.g. the page was opened mid-round): never played late.
      if (c.at + Math.min(c.ms, 400) < this.serverNow()) {
        this.handled.add(c.id);
        continue;
      }
      void this.load(c.src); // preload
      if (c.at - this.serverNow() > 4000) continue; // scheduled when closer
      this.handled.add(c.id);
      void this.play(c);
    }
  }

  private async play(c: BjCue) {
    const buffer = await this.load(c.src);
    if (!buffer || !this.ctx || !this.prefs.sound || this.ctx.state !== 'running') return;
    const delay = Math.max(0, (c.at - this.serverNow()) / 1000);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    const gain = this.ctx.createGain();
    gain.gain.value = c.kind === 'effect' ? 0.55 : 1;
    source.connect(gain).connect(this.ctx.destination);
    source.start(this.ctx.currentTime + delay);
  }

  private syncMusic() {
    if (this.prefs.music && this.prefs.sound && this.ctx?.state === 'running') void this.startMusic();
    else this.stopMusic();
  }

  private async startMusic() {
    if (this.music || !this.ctx) return;
    const buffer = await this.load(MUSIC);
    if (!buffer || !this.ctx || this.music) return;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const gain = this.ctx.createGain();
    gain.gain.value = 0.12;
    source.connect(gain).connect(this.ctx.destination);
    source.start();
    this.music = { source, gain };
  }

  private stopMusic() {
    try {
      this.music?.source.stop();
    } catch {
      // already stopped
    }
    this.music = null;
  }
}
